import type { ApiClient } from '@carbonwebmail/api-client'
import { useParams } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { AuthLayout } from '../../ui/AuthLayout.js'
import { PillButton } from '../../ui/PillButton.js'
import { useUnsubscribe } from './useUnsubscribe.js'

/**
 * The page the List-Unsubscribe link lands on.
 *
 * A confirmation step rather than an immediate unsubscribe, because corporate
 * gateways and link scanners follow every URL in a message before a human sees
 * it. The API refuses to unsubscribe on GET for the same reason, so this page
 * exists to turn a scanned link into a deliberate click.
 *
 * The two choices are given equal visual weight. Making "keep receiving" the
 * quiet option would be a dark pattern on the one screen where it matters most:
 * someone who wants to leave must not have to look for the way out.
 */
export function UnsubscribePage({ client }: { readonly client: ApiClient }) {
  const { token = '' } = useParams()
  const { state, confirm } = useUnsubscribe({ token, client })

  return (
    <AuthLayout>
      {state.status === 'done' ? (
        <div className="text-center">
          <h1 className="mb-2.5 font-bold text-[24px] text-t1">You have been unsubscribed</h1>
          <p className="text-[14px] text-t2">
            You will not receive further marketing email from this sender. It can take a few minutes
            for anything already being sent to stop.
          </p>
        </div>
      ) : (
        <div className="text-center">
          <h1 className="mb-2.5 font-bold text-[24px] text-t1">Unsubscribe from these emails?</h1>
          <p className="mb-6 text-[14px] text-t2">
            You will stop receiving marketing email from this sender. Receipts and account notices
            still reach you.
          </p>

          {state.status === 'failed' ? <Alert>{state.detail}</Alert> : null}

          <div className="flex justify-center gap-2.5">
            <div className="w-40">
              <PillButton
                type="button"
                variant="primary"
                onClick={confirm}
                disabled={state.status === 'working'}
              >
                {state.status === 'working' ? 'Unsubscribing…' : 'Unsubscribe'}
              </PillButton>
            </div>
            <div className="w-40">
              {/*
                Closing the tab is the honest action for "keep receiving": there
                is nothing to record, because nothing has changed. A button that
                posted something would be pretending.
              */}
              <PillButton type="button" variant="secondary" onClick={() => window.close()}>
                Keep receiving
              </PillButton>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  )
}
